import { SupportError } from './types.js'
import type { SupportEvent } from './types.js'
import { bytes, clone } from './report.js'
export interface Draft {
  id: string
  events: SupportEvent[]
  endedAt: number
  incomplete: boolean
  reason?: string
  shared: boolean
  updatedAt: number
}
const TTL = 24 * 60 * 60 * 1000
const LIMIT = 32 * 1024 * 1024
export class RecordingStore {
  private memory = new Map<string, Draft>()
  private db: IDBDatabase | null = null
  private queue: Promise<unknown> = Promise.resolve()
  private scope: string | null = null

  async open(scope: string) {
    if (typeof scope !== 'string' || !scope.trim() || scope.length > 200) throw new SupportError('InvalidOptions', 'A non-empty application/user storage scope is required')
    return this.serial(async () => {
    this.close()
    this.scope = scope
    this.db = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open('aeppic-support-v1', 1)
      request.onupgradeneeded = () => request.result.createObjectStore('drafts').createIndex('scope', 'scope')
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(new SupportError('StorageFailed', 'Could not open support storage'))
      request.onblocked = () => reject(new SupportError('StorageFailed', 'Support storage is blocked by another tab'))
    })
    this.db.onversionchange = () => this.close()
    for (const row of await this.rows()) if (row.updatedAt < Date.now() - TTL) await this.remove(row.id)
    })
  }
  useMemory() { return this.serial(async () => { this.close() }) }
  close() { this.db?.close(); this.db = null; this.scope = null }
  private key(id: string) { return JSON.stringify([this.scope, id]) }
  private transaction<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('drafts', mode)
      const request = action(tx.objectStore('drafts'))
      tx.oncomplete = () => resolve(request.result)
      tx.onerror = tx.onabort = () => reject(new SupportError('StorageFailed', 'Support storage transaction failed'))
    })
  }
  private serial<T>(fn: () => Promise<T>): Promise<T> {
    const result = this.queue.then(fn)
    this.queue = result.catch(() => {})
    return result
  }
  private async rows(): Promise<Draft[]> {
    if (!this.db) return [...this.memory.values()]
    const entries = await this.transaction('readonly', store => store.index('scope').getAll(this.scope)) as { scope: string; draft: Draft }[]
    return entries.filter(row => row.scope === this.scope).map(row => row.draft)
  }
  private async remove(id: string) {
    if (this.db) await this.transaction('readwrite', store => store.delete(this.key(id)))
    this.memory.delete(id)
  }
  async list(): Promise<Draft[]> {
    return this.serial(async () => {
      const result: Draft[] = []
      for (const draft of await this.rows()) {
        if (draft.updatedAt < Date.now() - TTL) await this.remove(draft.id)
        else result.push(clone(draft))
      }
      return result.sort((a, b) => b.updatedAt - a.updatedAt)
    })
  }
  async put(draft: Draft) {
    const copy = clone(draft)
    return this.serial(async () => {
      const size = bytes(copy)
      if (size > LIMIT) throw new SupportError('LimitReached', 'Recording exceeds the local storage budget')
      const rows = (await this.rows()).filter(row => row.id !== copy.id).sort((a, b) => a.updatedAt - b.updatedAt)
      let total = rows.reduce((sum, row) => sum + bytes(row), size)
      for (const row of rows) {
        if (total <= LIMIT && row.updatedAt >= Date.now() - TTL) break
        await this.remove(row.id)
        total -= bytes(row)
      }
      if (this.db) await this.transaction('readwrite', store => store.put({ scope: this.scope, draft: copy }, this.key(copy.id)))
      else this.memory.set(copy.id, copy)
    })
  }
  delete(id: string) { return this.serial(() => this.remove(id)) }
  clear() { return this.serial(async () => { for (const row of await this.rows()) await this.remove(row.id); this.memory.clear() }) }
}
